import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { useProject } from '../../contexts/ProjectContext';
import { formatDistanceToNow } from 'date-fns';
import NewProjectModal from './NewProjectModal';
import SafeIcon from '../../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiPlus, FiArrowRight, FiFolder } = FiIcons;

const ProjectOverview = () => {
  const { projects, isLoading, selectProject } = useProject();
  const [isModalOpen, setIsModalOpen] = useState(false);

  const recentProjects = projects.slice(0, 5);

  const formatDate = (dateString) => {
    try {
      return formatDistanceToNow(new Date(dateString), { addSuffix: true });
    } catch {
      return 'Unknown';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'live':
        return 'bg-green-100 text-green-800';
      case 'building':
        return 'bg-yellow-100 text-yellow-800';
      case 'draft':
        return 'bg-gray-100 text-gray-600';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };

  return (
    <>
      <div className="bg-white rounded-xl shadow-sm border border-slate-200 p-6">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-lg font-semibold text-slate-900">Projects</h2>
          <button
            onClick={() => setIsModalOpen(true)}
            className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors btn-focus"
          >
            <SafeIcon icon={FiPlus} className="w-4 h-4" />
            <span>New Project</span>
          </button>
        </div>

        {isLoading ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
          </div>
        ) : recentProjects.length === 0 ? (
          <div className="text-center py-12">
            <SafeIcon icon={FiFolder} className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-slate-900 mb-2">No projects yet</h3>
            <p className="text-slate-500 mb-6">Create your first project to get started with GYSO.</p>
            <button
              onClick={() => setIsModalOpen(true)}
              className="inline-flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <SafeIcon icon={FiPlus} className="w-4 h-4" />
              <span>Create Project</span>
            </button>
          </div>
        ) : (
          <>
            <div className="space-y-3">
              {recentProjects.map((project) => (
                <div
                  key={project.id}
                  className="flex items-center justify-between p-4 border border-slate-200 rounded-lg hover:bg-slate-50 transition-colors"
                >
                  <div className="flex-1">
                    <div className="flex items-center space-x-3">
                      <h3 className="font-medium text-slate-900">{project.project_name}</h3>
                      <span className={`px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(project.status)}`}>
                        {project.status}
                      </span>
                    </div>
                    <div className="text-sm text-slate-500 mt-1">
                      {project.business_type && `${project.business_type} • `}
                      Last updated {formatDate(project.updated_at)}
                    </div>
                  </div>
                  <button
                    onClick={() => selectProject(project)}
                    className="px-4 py-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors font-medium text-sm"
                  >
                    Open project
                  </button>
                </div>
              ))}
            </div>

            {projects.length > 5 && (
              <div className="mt-6 pt-4 border-t border-slate-200">
                <Link
                  to="/projects"
                  className="flex items-center justify-center space-x-2 text-blue-600 hover:text-blue-700 font-medium"
                >
                  <span>View all projects</span>
                  <SafeIcon icon={FiArrowRight} className="w-4 h-4" />
                </Link>
              </div>
            )}
          </>
        )}
      </div>

      <NewProjectModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </>
  );
};

export default ProjectOverview;